import { FieldError, Modal, TrackedButton } from '@automata/ui';
import {
  BooleanParameterDefinition,
  FileRefParameterDefinition,
  IntegerParameterDefinition,
  ParameterDefinition,
  StringParameterDefinition,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { Box, Stack } from '@mui/system';
import { useCallback, useMemo, useState } from 'react';

import {
  BooleanParameterFields,
  DurationParameterFields,
  FileParameterFields,
  NumericParameterFields,
  StringParameterFields,
} from './NewParameterTypeRenderer';
import { Errors, NumberParameterDefinition, ParameterType } from './types';
import { TypeSelect } from './TypeSelect';
import {
  allParameterTypes,
  createParameter,
  getParameterType,
  getParameterValidationErrors,
  getParameterValidationSchema,
} from './utils';
import { selectParameterDefinitions } from '../../../features/workflow/selectors';
import {
  addParameterDefinition,
  updateParameterDefinition,
} from '../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { TextFieldWithMaxLength } from '../../TextFieldWithMaxLength';

interface NewParameterReferenceModalProps {
  isEdit?: boolean;
  isTypeFixed?: boolean;
  parameter: ParameterDefinition;
  typeOptions: ParameterType[];
  onClose: (paramId?: string) => void;
}

export const NewParameterReferenceModal = ({
  isEdit = false,
  isTypeFixed = false,
  typeOptions,
  parameter: savedParameter,
  onClose,
}: NewParameterReferenceModalProps) => {
  const dispatch = useAppDispatch();
  const [parameter, setParameter] =
    useState<ParameterDefinition>(savedParameter);
  const [errors, setErrors] = useState<Errors>({});
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const parameters = useAppSelector(selectParameterDefinitions);
  const existingNames = useMemo(
    () => parameters.filter((p) => p.id !== parameter.id).map((p) => p.name),
    [parameter, parameters]
  );

  const submitBtnText = useMemo(() => {
    if (isTypeFixed) return 'Create & Apply';

    return isEdit ? 'Save' : 'Create';
  }, [isEdit, isTypeFixed]);

  const handleFieldChange = useCallback(
    <T = ParameterDefinition,>(field: keyof T, value: T[keyof T]) => {
      const type = getParameterType(parameter);
      const schema = getParameterValidationSchema(type);
      const parsed = schema.safeParse({
        ...parameter,
        existingNames,
        [field]: value,
      });

      const newErrors = getParameterValidationErrors(parsed);
      setErrors(newErrors);
      setIsFormValid(Object.values(newErrors).every((err) => !err));

      setParameter((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [existingNames, parameter]
  );

  const handleCreateParameter = useCallback(() => {
    dispatch(
      isEdit
        ? updateParameterDefinition(parameter)
        : addParameterDefinition(parameter)
    );
    onClose(parameter.id);
  }, [isEdit, parameter, dispatch, onClose]);

  const handleChangeType = useCallback((type: ParameterType) => {
    const newParameter = createParameter(type);
    setParameter(newParameter);
  }, []);

  return (
    <Modal
      open
      title={isEdit ? 'Edit parameter reference' : 'New parameter reference'}
      onClose={onClose}
    >
      <Stack minHeight={300}>
        <Box margin="auto" textAlign="left" flex={1} width="100%">
          <Stack spacing={2} marginBottom={2}>
            <TypeSelect
              disabled={isEdit || (isTypeFixed && typeOptions.length <= 1)}
              label="type"
              typeOptions={typeOptions ?? allParameterTypes}
              value={getParameterType(parameter)}
              onChange={(e) => handleChangeType(e)}
            />
            <Box>
              <TextFieldWithMaxLength
                error={!!errors.name}
                fullWidth
                label="name"
                maxLength={50}
                value={parameter.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
              <FieldError error={errors.name?.message} />
            </Box>
            {(parameter.type === 'integer' || parameter.type === 'float') &&
              !parameter.id.endsWith('_duration') && (
                <NumericParameterFields
                  errors={errors}
                  parameter={parameter}
                  onChange={handleFieldChange<NumberParameterDefinition>}
                />
              )}
            {parameter.type === 'string' && (
              <StringParameterFields
                errors={errors}
                parameter={parameter}
                onChange={handleFieldChange<StringParameterDefinition>}
              />
            )}
            {parameter.type === 'boolean' && (
              <BooleanParameterFields
                parameter={parameter}
                onChange={handleFieldChange<BooleanParameterDefinition>}
              />
            )}
            {parameter.type === 'integer' &&
              parameter.id.endsWith('_duration') && (
                <DurationParameterFields
                  errors={errors}
                  parameter={parameter}
                  onChange={handleFieldChange<IntegerParameterDefinition>}
                />
              )}
            {parameter.type === 'file_ref' && (
              <FileParameterFields
                errors={errors}
                parameter={parameter}
                onChange={handleFieldChange<FileRefParameterDefinition>}
              />
            )}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <TrackedButton
              trackLabel="workflowBuilder.newParameterReferenceModal.Cancel"
              color="primary"
              variant="text"
              onClick={() => onClose()}
            >
              Cancel
            </TrackedButton>
            <TrackedButton
              trackLabel="workflowBuilder.newParameterReferenceModal.Create"
              color="primary"
              variant="contained"
              onClick={handleCreateParameter}
              disabled={!isFormValid}
            >
              {submitBtnText}
            </TrackedButton>
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
};
